import {Component, computed, effect, inject, input, viewChild} from '@angular/core';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {DatePipe} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {AdxPaymentDetailsModalComponent} from 'src/app/adx-payment/components/adx-payment-details-modal/adx-payment-details-modal.component';
import {PaymentFormComponent} from '../../../payments/components/payment-form/payment-form.component';
import {GridEventOutput, GridModel} from 'src/app/shared/model/page-grid';
import {SearchPaginatedAdxPaymentResponse} from '../../model/search-paginated-adx-payment-response';
import {AdxPaymentByIdResponse} from '../../model/adx-payment-by-id-response';
import {AdxPaymentGridService} from '../../service/adx-payment-grid.service';
import {PageGridComponent} from 'src/app/shared/components/page-grid/page-grid.component';
import {MaskedCardPipe} from 'src/infrastructure/pipes/masked-card.pipe';
import {AzureAuthService} from 'src/infrastructure/services/azure-auth.service';

@Component({
    selector: 'app-adx-payment-grid',
    templateUrl: './adx-payment-grid.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class AdxPaymentGridComponent {
  public data = input<any>();
  pageGrid = viewChild(PageGridComponent);

  private adxPaymentGridService = inject(AdxPaymentGridService);
  private azureAuthService = inject(AzureAuthService);
  private datePipe = inject(DatePipe);
  private dialog = inject(MatDialog);
  private maskedCardPipe = inject(MaskedCardPipe);

  constructor() {
    effect(() => {
      const dataValue = this.data();
      const hasRole = this.loggedInUserHasDirectorOrFinanceOrManagerRole();
      const dataSourceId = dataValue?.trip?.dataSourceId;
      
      if (hasRole) {
        if (dataSourceId != null) {
          this.adxPaymentsGrid.eventButton = { text: 'Add', icon: 'add', disabled: true, tooltip: 'Create payment for trip is temporarily disabled.' };
        } else {
          this.adxPaymentsGrid.eventButton = { 
            text: 'Add', 
            icon: 'add', 
            disabled: true, 
            tooltip: 'Cannot create payment for trip with no data source' 
          };
        }
      } else {
        this.adxPaymentsGrid.eventButton = undefined;
      }
    });
  }
  protected readonly loggedInUserHasDirectorOrFinanceOrManagerRole = computed(() => {
    const hasRole = this.azureAuthService.checkUserAzureTokenHasManagerRole() && this.azureAuthService.checkUserAzureTokenHasFinanceRole() && this.azureAuthService.checkUserAzureTokenHasDirectorRole();
    return hasRole;
  });

  protected readonly shouldShowAddButton = computed(() => {
    const dataValue = this.data();
    const hasRole = this.loggedInUserHasDirectorOrFinanceOrManagerRole();
    const dataSourceId = dataValue?.trip?.dataSourceId;
    const shouldShow = hasRole && dataSourceId != null;
    return shouldShow;
  });

  protected readonly adxPaymentsGrid: GridModel<SearchPaginatedAdxPaymentResponse> = {
    gridDataTestId: 'adx-payment-list',
    gridTitle: 'Payments',
    replayButton: true,
    eventButton: undefined,
    isTotalsRowHidden: true,
    defaultSort: { active: 'dateUpdated', direction: 'desc' },
    columns: [
      {
        columnProperty: 'fmsAdxPaymentDetailId',
        columnHeader: 'Payment Fms Number',
        sort: true,
        clickable: true,
        style: () => ({ 'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer' }),
        linkEventFunction: (item: AdxPaymentByIdResponse) => `AdxPayments/${item.fmsAdxPaymentDetailId}`,
      },
      {
        columnProperty: 'payeeName',
        columnHeader: 'Payee Name',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'vendorName',
        columnHeader: 'Vendor Name',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'payTypeName',
        columnHeader: 'Pay Type',
        sort: true,
      },
      {
        columnProperty: 'payMethodName',
        columnHeader: 'Pay Method',
        sort: true,
      },
      {
        columnProperty: 'profileTypeName',
        columnHeader: 'Profile Type',
        sort: true,
      },
      {
        columnProperty: 'amount',
        columnHeader: 'Amount',
        sort: true,
      },
      {
        columnProperty: 'ckCcNo',
        columnHeader: 'Payment Reference Number',
        sort: true,
        columnPropertyFunction: (item: AdxPaymentByIdResponse) => item.ckCcNo ? this.maskedCardPipe.transform(item.ckCcNo) : item.ckCcNo
      },
      {
        columnProperty: 'branchName',
        columnHeader: 'Branch',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'bankName',
        columnHeader: 'Bank',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'issueDate',
        columnHeader: 'Payment Date',
        sort: true,
        columnPropertyFunction: (item: AdxPaymentByIdResponse) => this.datePipe.transform(item.paymentDate, 'M/d/y') || '',
      },
      {
        columnProperty: 'tripExternalReference',
        columnHeader: 'Trip Record Locator',
        sort: true,
        clickable: true,
        style: () => ({ 'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer' }),
        linkEventFunction: (item: AdxPaymentByIdResponse) => `Trip/${item.tripId}`,
      },
      {
        columnProperty: 'adxInvoiceId',
        columnHeader: 'Invoice Number',
        sort: true,
        clickable: true,
        style: () => ({ 'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer' }),
        linkEventFunction: (item: AdxPaymentByIdResponse) => `AdxInvoices/${item.adxInvoiceId}`,
      },
    ],
    filters: [],
    gridGetService: this.adxPaymentGridService
  }

  ngOnInit(): void {
    const dataValue = this.data();
    if (dataValue == undefined) {
    }

    this.adxPaymentsGrid.filtersInitial = {
      tripId: dataValue?.tripId || null,
    }
  }

  protected handleCreatePaymentDialog(): void {
    this.dialog
      .open(PaymentFormComponent, { data: { dataSourceId: this.data()?.trip?.adxInvoices?.[0]?.tramsDataSource?.id } })
      .afterClosed().subscribe((saved)=>{
        if(saved){
          this.pageGrid()?.getItems();
        }
      });
  }

  handleIdClick(event: GridEventOutput<AdxPaymentByIdResponse>): void {
    if(event.column === 'fmsAdxPaymentDetailId' && event.item.fmsAdxPaymentDetailId !== undefined){
      this.openPaymentDialog(event.item.fmsAdxPaymentDetailId, `Payment Information`);
    }
    else if(event.column === 'tripExternalReference' && event.item.tripId !== undefined){
      window.location.href = `Trip/${event.item.tripId}`;
    }
    else if(event.column === 'adxInvoiceId' && event.item.adxInvoiceId !== undefined){
      window.location.href = `AdxInvoices/${event.item.adxInvoiceId}`;
    }
  }

  public openPaymentDialog(adxPaymentId: number, title: string): void {
    this.dialog.open(AdxPaymentDetailsModalComponent, {
      width: '55%',
      data: { adxPaymentId: adxPaymentId, title: title }
    });
  }
}
